import { memo } from 'react'
import { ButtonBase, SxProps } from '@mui/material'
import { BookSeatsLogo, HamburgerIcon, PlusIcon } from '@/assets'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { useTranslations } from '@/core/translation'
import { SidebarLayoutProps } from './SidebarLayout.types'
import { useSidebarLayoutController } from './SidebarLayout.controller'
import { sidebarLayoutStyles as styles } from './SidebarLayout.style'
import SidebarBrand from './SidebarBrand'
import SidebarHistory from './SidebarHistory'
import CurrencySelector from './CurrencySelector'
import SidebarProfile from './SidebarProfile'

function SidebarLayout({ children }: SidebarLayoutProps) {
  const t = useTranslations('sidebar')
  const {
    isExpanded,
    asideVariant,
    isRail,
    showLabels,
    showTopBar,
    currencyCode,
    conversations,
    activeConversationId,
    onToggle,
    onClose,
    onCurrencyChange,
    onOpenConversation,
    onRemoveConversation,
    onClearConversations,
    onNewSearch,
  } = useSidebarLayoutController()

  return (
    <Box sx={styles.root}>
      {asideVariant === 'mobile' && isExpanded && <Box sx={styles.backdrop} onClick={onClose} />}

      <Box component="aside" sx={asideStyles(asideVariant, isExpanded) as SxProps}>
        <SidebarBrand showLabel={showLabels} onToggle={onToggle} />

        <Box sx={styles.newSearchWrap}>
          <ButtonBase
            sx={
              [
                styles.newSearch,
                isRail && styles.newSearchRail,
                isRail && !isExpanded && styles.newSearchRound,
              ] as SxProps
            }
            onClick={onNewSearch}
            aria-label={t('newSearch')}
            title={showLabels ? undefined : t('newSearch')}>
            <Box sx={[styles.itemIconSlot, isRail && styles.itemIconSlotRail] as SxProps}>
              <Box component={PlusIcon} sx={styles.newSearchIcon} />
            </Box>
            {showLabels && (
              <Text component="span" sx={styles.newSearchLabel}>
                {t('newSearch')}
              </Text>
            )}
          </ButtonBase>
        </Box>

        {showLabels ? (
          <SidebarHistory
            items={conversations}
            activeId={activeConversationId}
            onOpen={onOpenConversation}
            onRemove={onRemoveConversation}
            onClear={onClearConversations}
          />
        ) : (
          <Box sx={styles.railSpacer} />
        )}

        <Box sx={styles.footer}>
          {/* The shape follows the width so the corners un-round as the panel
              opens; the label waits until it has finished. */}
          <CurrencySelector
            code={currencyCode}
            isRail={isRail}
            isRound={isRail && !isExpanded}
            showLabel={showLabels}
            onChange={onCurrencyChange}
          />
          <SidebarProfile
            isRail={isRail}
            isRound={isRail && !isExpanded}
            showLabel={showLabels}
            onNavigate={onClose}
          />
        </Box>
      </Box>

      <Box sx={styles.main}>
        {showTopBar && (
          <Box sx={styles.topBar}>
            <ButtonBase sx={styles.iconButton} onClick={onToggle} aria-label={t('toggle')}>
              <Box component={HamburgerIcon} sx={styles.iconButtonIcon} />
            </ButtonBase>
            <Box component={BookSeatsLogo} sx={styles.topBarBrand} />
          </Box>
        )}
        <Box sx={styles.content}>{children}</Box>
      </Box>
    </Box>
  )
}

/** Panel styles for the current breakpoint and open state. */
function asideStyles(variant: 'mobile' | 'desktop', isExpanded: boolean) {
  return variant === 'mobile'
    ? [styles.asideBase, styles.asideMobile, !isExpanded && styles.asideMobileClosed]
    : [styles.asideBase, styles.asideDesktop, isExpanded && styles.asideDesktopExpanded]
}

export default memo(SidebarLayout)
